import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {FolderSection} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import APIUtil from 'util/APIUtil';

import {ArchivableItem, OTHER_PURPOSE, MaybeArchivableItem, ArchivableItemGQL} from './ArchiveConstants';
import isArchived, {isArchivedGQL, isArchivedREST, isCoreObjectArchived} from './isArchived';

type ItemId = string | number | undefined;

// Keep in sync with benchling/archive/validate:_is_non_data
function isNonData(itemId: ItemId): boolean {
  // Only non-data are possibly not APIIdentified, e.g. SchemaField.
  if (typeof itemId !== 'string') {
    return true;
  }
  return [
    ModelTypeConstants.ASSAY_SCHEMA,
    ModelTypeConstants.BATCH_SCHEMA,
    ModelTypeConstants.BENCHLING_APP,
    ModelTypeConstants.BOX_SCHEMA,
    ModelTypeConstants.CONTAINER_SCHEMA,
    ModelTypeConstants.CONNECTION_SCHEMA,
    ModelTypeConstants.LIBRARY_PARAMETER_DEFINITION,
    ModelTypeConstants.LOCATION_SCHEMA,
    ModelTypeConstants.PLATE_SCHEMA,
    ModelTypeConstants.PROCESS_SCHEMA,
    ModelTypeConstants.PROCESS_TASK_SCHEMA,
    ModelTypeConstants.REQUEST_TASK_SCHEMA,
    ModelTypeConstants.REVIEW_LIFECYCLE,
    ModelTypeConstants.SAMPLE_TYPE,
    ModelTypeConstants.SCHEMA_FIELD,
    ModelTypeConstants.SCHEMA_FIELD_SELECTOR,
    ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION,
    ModelTypeConstants.STUDY_SCHEMA,
    ModelTypeConstants.TAG_SCHEMA,
    ModelTypeConstants.TEST_DEFINITION,
    ModelTypeConstants.MONOMER,
  ].includes(APIUtil.apiIdToType(itemId));
}

// Keep in sync with benchling/archive/validate:_is_expendable
function isExpendable(itemId: ItemId): boolean {
  // All expendables are APIIdentified, so it's safe to assume any non-expendables
  // are not (e.g. SchemaField).
  if (typeof itemId !== 'string') {
    return false;
  }
  return (
    APIUtil.isBiologicalEntity(itemId) ||
    [
      ModelTypeConstants.BATCH,
      ModelTypeConstants.PLATE,
      ModelTypeConstants.BOX,
      ModelTypeConstants.CONTAINER,
    ].includes(APIUtil.apiIdToType(itemId))
  );
}

// Keep in sync with benchling/archive/validate:_is_run_related_or_result
function isRunRelatedOrResult(itemId: ItemId): boolean {
  if (typeof itemId !== 'string') {
    return false;
  }
  return [ModelTypeConstants.ASSAY_RUN, ModelTypeConstants.AUTOMATION_OUTPUT_FILE].includes(
    APIUtil.apiIdToType(itemId)
  );
}

/**
 * Returns the archive purposes that the backend will accept for the items
 *
 * NOTE: Keep in sync with benchling/archive/validate:get_valid_purposes_for_class
 */
export function getValidArchivePurposes(itemIds: Array<ItemId>): Array<string> {
  let purposes: Array<string>;
  if (!itemIds.length) {
    purposes = [];
  } else if (itemIds.every((itemId) => isNonData(itemId))) {
    purposes = BenchlingConfig.ARCHIVE_NON_DATA_PURPOSES;
  } else if (itemIds.every((itemId) => isExpendable(itemId))) {
    purposes = BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES;
  } else if (itemIds.every((itemId) => isRunRelatedOrResult(itemId))) {
    purposes = BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES;
  } else {
    purposes = BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES;
  }
  if (!purposes.includes(OTHER_PURPOSE)) {
    purposes = [...purposes, OTHER_PURPOSE];
  }
  return purposes;
}

/**
 * Returns the archive purposes that the user can select when archiving or filtering items
 */
export function getVisibleArchivePurposes(itemIds: Array<ItemId>): Array<string> {
  if (!itemIds.length) {
    return [];
  }
  if (itemIds.every((itemId) => isRunRelatedOrResult(itemId))) {
    return BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES;
  }
  if (itemIds.every((itemId) => isNonData(itemId))) {
    return BenchlingConfig.ARCHIVE_NON_DATA_PURPOSES;
  }
  const isAnyItemExpendable = itemIds.some((itemId) => isExpendable(itemId));
  if (isAnyItemExpendable) {
    return _.union(
      BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES,
      BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES
    );
  }
  return BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES;
}

/**
 * Returns whether archive purposes are visible to the user
 *
 * When archive purposes are not visible, we don't show purposes for the user to select when archiving and
 * all archived items are labeled with "Archived" instead of the underlying system archive reason
 */
export function areArchivePurposesVisible(itemIds: Array<ItemId>): boolean {
  return getVisibleArchivePurposes(itemIds).length > 0;
}

export function getAllArchivePurposes(): Array<string> {
  // eslint-disable-next-line no-restricted-syntax
  return _.union(
    BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES,
    BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES,
    [OTHER_PURPOSE]
  ).sort();
}

export function getArchivePurpose(item: ArchivableItem): string | null {
  if (isArchivedREST(item) || isArchivedGQL(item)) {
    return item.archiveRecord?.purpose ?? null;
  } else if (isCoreObjectArchived(item)) {
    return item.archiveData.purpose;
  } else {
    return null;
  }
}

export function getArchiveText(item: ArchivableItem): string {
  return areArchivePurposesVisible([item.id]) ? getArchivePurpose(item)! : 'Archived';
}

export function getArchiveItemName(item: MaybeArchivableItem): string {
  if (!isArchived(item)) {
    return item.name;
  }
  return `${item.name} (${getArchiveText(item)})`;
}

export function getPurposesForFolderSection(folderSection: FolderSection | null): Array<string> {
  switch (folderSection) {
    case FolderSection.INVENTORY:
      return BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES;
    case FolderSection.NOTEBOOK:
      return BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES;
    case FolderSection.ALL:
      // eslint-disable-next-line no-restricted-syntax
      return _.uniq([
        ...BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES,
        ...BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES,
      ]);
    default:
      // eslint-disable-next-line no-restricted-syntax
      return _.uniq([
        ...BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES,
        ...BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES,
      ]);
  }
}

export function attachArchivePurpose<T extends ArchivableItemGQL>(
  model: T,
  purpose: string | null | undefined
): T {
  return {
    ...model,
    archiveRecord: purpose != null ? {__typename: 'ArchiveRecordNode', purpose} : null,
  };
}
